'use client';

import { type ReactNode, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { STEP_SECONDS, useVideoSeekKeys } from '@/hooks/use-video-seek-keys';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

const PlayIcon = () => (
  <svg
    className="h-3 w-3"
    viewBox="0 0 12 12"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M2.5 1.5v9l8-4.5z" />
  </svg>
);

/**
 * Centered hero: centered headline + subheadline and a 16:9 media card with a
 * click-to-play "Watch video" pill. This is the primary marketing hero used on
 * the homepage and industry landing pages (the legacy hero lives in hero.tsx).
 *
 * Responsive type stops: h1 32/36/48px at <810 / 810-1199 / ≥1200 (mapped to
 * base/md/xl); sub Inter 14/16/18px at the same stops; media card 16:9, square
 * corners, capped at 1152px (max-w-6xl).
 */

interface HeroCenteredProps {
  /** Headline. Defaults to the home hero title. */
  title?: ReactNode;
  /** Subheadline. Defaults to the home hero subtitle. */
  subtitle?: ReactNode;
  /** Media card video. Defaults to the home hero film — Arabic cut on /ar. */
  videoSrc?: string;
  /** Frame (seconds) used as the resting poster and playback start. */
  videoStart?: number;
  /**
   * Still shown before playback. Worth supplying whenever the film has a master
   * to cut it from: the codec spends fewest bits on the opening frames, so the
   * frame the video itself parks on is its weakest. Falls back to that frame.
   */
  posterSrc?: string;
  /** Primary CTA label. Defaults to "Get a demo". */
  ctaText?: string;
  /** Primary CTA href. Defaults to /demo. */
  ctaHref?: string;
  /**
   * Opt in to the launch flag pinned to the top-left of the media card —
   * the App Builder announcement and its release date. Off by default: the
   * other pages using this hero run a different film, and the flag names
   * what is playing.
   */
  launchBanner?: boolean;
}

const HeroCentered = ({
  title,
  subtitle,
  videoSrc,
  videoStart,
  posterSrc,
  ctaText,
  ctaHref = '/demo',
  launchBanner = false,
}: HeroCenteredProps = {}) => {
  const { t, locale } = useTranslation();
  // Same hero film, Arabic-language cut on /ar. An explicit videoSrc prop still wins.
  const usesArabicCut = !videoSrc && locale === 'ar';
  // The English cut is likewise only the default when no film was passed in.
  const usesEnglishCut = !videoSrc && locale !== 'ar';
  const resolvedVideoSrc =
    videoSrc ??
    (locale === 'ar'
      ? '/home/hero-pricing-ar-2160p.mp4'
      : '/home/hero-apps.mp4');
  // The English cut opens on the "SaaS is dead" montage and its red
  // NO / NO IT'S / NOT titles (last titled frame 8.98s), then holds a squinting
  // beat through to 13s. Playback skips all of it: 13.00s is the first frame
  // where he is square to camera, eyes open and mouth shut, with his line
  // landing 55ms later. The Arabic cut has no such opener and starts at the top.
  const resolvedVideoStart = videoStart ?? (usesArabicCut ? 0 : 13);
  // Both default cuts park on a chosen still. The English film's is cut at
  // 13.00s — the same frame playback opens on, so the card does not jump when
  // it starts. Films passed in via videoSrc bring their own or go without.
  const resolvedPosterSrc =
    posterSrc ??
    (usesArabicCut
      ? '/home/hero-pricing-ar-poster.jpg'
      : usesEnglishCut
        ? '/home/hero-apps-poster.jpg'
        : undefined);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  // Left/right arrows scrub by ten seconds while the video is playing.
  const seek = useVideoSeekKeys(videoRef, playing);
  // True once the film has been started at least since it was last reset, so a
  // resume picks up where the viewer paused instead of seeking back to the top.
  const hasStartedRef = useRef(false);

  const labels =
    locale === 'ar'
      ? {
          seeHow: 'ورّيني كيف يشتغل',
          watch: 'شغّل الفيديو',
          pause: 'إيقاف',
          pauseAria: 'أوقف الفيديو',
          watchAria: 'شغّل فيديو المنتج',
          launchEyebrow: 'الإطلاق',
          // Name as the /app-builder page sets it, so the two pages agree.
          launchName: 'بنّاء التطبيقات',
          // Arabic-Indic digits, as everywhere else on /ar.
          launchDate: '١٦ سبتمبر ٢٠٢٦',
        }
      : {
          seeHow: 'See how it works',
          watch: 'Watch the launch',
          pause: 'Pause',
          pauseAria: 'Pause video',
          watchAria: 'Watch the launch',
          launchEyebrow: 'New',
          launchName: 'App Builder',
          launchDate: 'September 16, 2026',
        };

  // "See how it works" scrolls the media card into view and plays it.
  const showHowItWorks = () => {
    videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    void handleWatch();
  };

  const handleWatch = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (!video.paused) {
      video.pause();
      setPlaying(false);
      return;
    }
    // The first press begins on the configured start frame (skips the intro).
    // Every press after that is a resume, so leave the playhead alone.
    if (!hasStartedRef.current) {
      try {
        video.currentTime = resolvedVideoStart;
      } catch {
        /* seek may be deferred until metadata loads — harmless */
      }
      hasStartedRef.current = true;
    }
    // No native controls — the overlay play/pause button drives it.
    // Unmuted playback needs a user-gesture token; if the browser withholds
    // one (e.g. automated clicks), retry muted so the video still plays.
    try {
      video.muted = false;
      await video.play();
      setPlaying(true);
    } catch {
      try {
        video.muted = true;
        await video.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    }
  };

  return (
    <section className="bg-[#fafaf9] text-neutral-900">
      <div className="container flex min-h-screen flex-col items-center justify-center pt-32 pb-16 lg:pt-40">
        {/* Headline — responsive stops: 32 / 36 / 48px */}
        <h1 className="max-w-3xl text-center font-serif text-[2rem] text-balance md:text-4xl xl:text-5xl">
          {title ?? t.hero['title']}
        </h1>

        {/* Subheadline — 14 / 16 / 18px, stone-500 (#78716c) */}
        <p className="mt-4 max-w-3xl text-center text-sm whitespace-pre-wrap text-[#78716c] md:text-base xl:text-lg">
          {subtitle ??
            t.hero[
              'Automate 77% of customer support—inbound, outbound, voice, telephony, QA, VoC. Purpose built for the enterprise.'
            ]}
        </p>

        {/* CTAs — primary (black) + secondary outline with a play icon */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button
            asChild
            className="h-11 bg-neutral-900 px-6 text-sm font-medium text-white hover:bg-neutral-800"
          >
            <Link
              href={ctaHref}
              prefetch
              aria-label={
                ctaHref === '/demo'
                  ? t.common['Learn more about Open and book a demo']
                  : undefined
              }
            >
              {ctaText ?? t.nav['Get a demo']}
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={showHowItWorks}
            className="h-11 border-neutral-300 bg-white px-6 text-sm font-medium text-neutral-900 hover:bg-neutral-50"
          >
            <PlayIcon />
            {labels.seeHow}
          </Button>
        </div>

        {/* Video card — 16:9, square corners, capped at 1152px */}
        <div className="radius-18 relative mt-10 w-full max-w-6xl overflow-hidden bg-neutral-100 md:mt-14">
          <video
            ref={videoRef}
            // #t=<videoStart> is what stops the card parking on a black box
            // when there is no still. With one, the fragment is left off: it
            // would make the browser decode and paint that frame over a poster
            // deliberately cut from somewhere else in the film. handleWatch
            // seeks to videoStart on the first press either way.
            src={
              resolvedPosterSrc
                ? resolvedVideoSrc
                : `${resolvedVideoSrc}#t=${resolvedVideoStart}`
            }
            poster={resolvedPosterSrc}
            playsInline
            preload="metadata"
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            onEnded={() => {
              // Film ran out: the next press is a fresh start, not a resume.
              hasStartedRef.current = false;
              setPlaying(false);
            }}
            className="aspect-video h-auto w-full object-cover"
          />
          {/* Launch flag — names the film and when it shipped. Sits above the
           *  overlay play button (z-20) and takes no pointer events, so the
           *  whole card stays one click target. Logical `start-4` so it hangs
           *  off the leading edge in both scripts. */}
          {launchBanner && (
            <div className="pointer-events-none absolute start-4 top-4 z-20 flex items-center gap-2 rounded-full bg-neutral-900/80 py-1.5 ps-1.5 pe-3.5 text-xs text-white shadow-lg backdrop-blur-sm">
              <span className="rounded-full bg-white px-2 py-0.5 font-medium text-neutral-900">
                {labels.launchEyebrow}
              </span>
              <span className="font-medium">{labels.launchName}</span>
              <span aria-hidden className="text-white/40">
                ·
              </span>
              <span className="text-white/70">{labels.launchDate}</span>
            </div>
          )}

          {/* Scrub readout. Sits above the overlay button so a press reads as
           *  feedback on the video rather than on the play control, and keyed
           *  on seq so pressing the same arrow twice replays the fade instead
           *  of looking inert.
           *
           *  Arabic-Indic digits on /ar, matching the rest of the Arabic site
           *  (٧٧٪, ٢-٣ أسابيع). dir=ltr because the sign belongs to the left of
           *  the number in both scripts — bidi would otherwise push a leading
           *  "+" to the far side in RTL.
           *
           *  aria-hidden: a screen reader user gets the seek from the media
           *  itself, and announcing "+10" on every press would be noise. */}
          {seek && (
            <span
              key={seek.seq}
              aria-hidden
              dir="ltr"
              className="pointer-events-none absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-neutral-900/80 px-4 py-2 text-lg font-medium text-white tabular-nums backdrop-blur-sm duration-300 animate-in fade-in zoom-in-95"
            >
              {seek.direction > 0 ? '+' : '\u2212'}
              {new Intl.NumberFormat(
                locale === 'ar' ? 'ar-u-nu-arab' : 'en-US',
              ).format(STEP_SECONDS)}
            </span>
          )}

          <button
            type="button"
            onClick={handleWatch}
            aria-label={playing ? labels.pauseAria : labels.watchAria}
            className={cn(
              // radius-18 to match the video it covers. This button is a
              // transparent hit area, so its radius is invisible until the
              // focus ring traces it — as a pill that was a ~311px stadium
              // drawn across the hero.
              'radius-18 absolute inset-0 flex items-center justify-center transition-opacity',
              playing && 'opacity-0 hover:opacity-100',
            )}
          >
            <span
              data-slot="button"
              className="inline-flex items-center gap-2 bg-neutral-900 px-5 py-2.5 text-sm text-white shadow-lg transition-transform hover:scale-105"
            >
              {playing ? (
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 12 12"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M2 1h3v10H2zM7 1h3v10H7z" />
                </svg>
              ) : (
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 12 12"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M2.5 1.5v9l8-4.5z" />
                </svg>
              )}
              {playing ? labels.pause : labels.watch}
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default HeroCentered;
